import React, { Fragment } from 'react';
import { Button } from 'react-bootstrap';
import { FilterFieldType } from './CustomFilter';
import { Trash } from '@icons';
import { parseQueryParams, wrapQueryParams } from './queryParamsUtils';
// import useFilterRedirect from './useFilterRedirect';

interface FiltersToDisplayProps {
	queryParams: string;
	setQueryParams: React.Dispatch<React.SetStateAction<string>>;
	activeFilters: { [key: string]: string[] };
	setActiveFilters: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
	filterFields: FilterFieldType[];
	isCompleteList: boolean;
	myUser: { firstName: string; lastName: string; id: string };
	searchRef: React.RefObject<{ reset: () => void }>;
}

const FiltersToDisplay: React.FC<FiltersToDisplayProps> = ({
	queryParams,
	activeFilters,
	setActiveFilters,
	filterFields,
	setQueryParams,
	isCompleteList,
	myUser,
	searchRef,
}) => {
	// console.log('searchRef', searchRef.current);
	// const handleFilterRedirect = useFilterRedirect();

	const filtersToHide = ['sampling', 'entity_type', 'invoicepayment_has_document'];

	const filtersToDisplay = Object.entries(parseQueryParams(queryParams)).filter(([key]) => !filtersToHide.includes(key));

	const getAdvancedOptionLabelByKeyAndValue = (key: string, value: string) => {
		if (key === 'term') return 'Ricerca testuale';
		if (key.endsWith('has_document')) {
			// const suffix = value === '1' ? 'contenenti documenti' : 'non contenenti documenti';
			const suffix = '- Documenti';

			switch (true) {
				case key.startsWith('tender'):
					return `Procedure ${suffix}`;
				case key.startsWith('frameworkContract'):
					return `Contratti aperti ${suffix}`;
				case key.startsWith('contract'):
					return `Incarichi/attingimenti ${suffix}`;
				case key === 'invoicepayment_has_document':
					return `Pagamenti ${suffix}`;
				case key === 'invoice_has_document':
					return `Fatture ${suffix}`;
				default:
					return 'Unknown key';
			}
		}

		if (key.endsWith('question_code')) {
			const prefix = 'Domande contenute';
			switch (true) {
				case key.startsWith('tenders'):
					return `${prefix} nelle procedure`;
				case key.startsWith('frameworkcontracts'):
					return `${prefix} nei contratti aperti`;
				case key.startsWith('contracts'):
					return `${prefix} negli incarichi/attingimenti`;
				case key === 'invoices_question_code':
					return `${prefix} nelle fatture`;
				case key === 'invoicepayments_question_code':
					return `${prefix} nei pagamenti`;
				default:
					return 'Unknown key';
			}
		}

		if (key === 'document_codes') {
			switch (value) {
				case 'PA_AVVISO':
					return `Avviso di pagamento`;
				case 'PA_GIUSTIFICATIVO':
					return `Giustificativo di pagamento`;
				default:
					return 'Unknown key';
			}
		}

		return 'Unknown key';
	};

	const getBooleanOptionLabelByKeyAndValue = (key: string, value: string) => {
		if (key === 'term') return decodeURIComponent(value);
		if (key === 'document_codes') {
			switch (value) {
				case 'PA_AVVISO':
				case 'PA_GIUSTIFICATIVO':
					const mainValue = activeFilters['invoicepayment_has_document']?.[0];
					const label = filterFields
						.find((field) => field.name === 'invoicepayment')
						.advancedOptions?.find((advancedOption) => advancedOption.name === 'has_document')
						?.options.find((option) => option.value === mainValue)?.label;
					return label || 'Unknown value';
			}
		}

		const standardValue = filterFields
			.find((field) => field.name === key || field.queryParamName === key)
			?.options.find((option) => option.value === value)?.label;

		if (standardValue) return standardValue;

		const advancedValue = filterFields
			.find((field) => field.name === key.split('_')[0])
			.advancedOptions?.find((advancedOption) => advancedOption.name === key.split('_').splice(1).join('_'))
			.options.find((option) => option.value === value)?.label;
		if (advancedValue) return advancedValue;
		return 'Unknown value';
	};
	return filtersToDisplay.length > 0 ? (
		<div
			style={{
				minHeight: '80px',
				height: 'auto',
				display: 'grid',
				gridTemplateColumns: 'auto auto 1fr',
				gap: '1rem',
				marginBottom: '3rem',
			}}
		>
			<div
				style={{
					minHeight: '80px',
					height: 'auto',
					width: '4px',
					backgroundColor: '#DEE4F1',
				}}
			/>
			<h5 className="fw-bold mt-2">Filtri applicati:</h5>
			<div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'start', justifyContent: 'start' }}>
				{filtersToDisplay.map(([key, values]) => (
					<Fragment key={key}>
						{values.map((value) => (
							<Button
								onClick={() => {
									setActiveFilters((prevActiveFilters) => {
										const newFilters = { ...prevActiveFilters };
										if (newFilters[key]) {
											newFilters[key] = newFilters[key].filter((item) => item !== value);
											if (newFilters[key].length === 0) {
												delete newFilters[key];
											}
										}
										if (key === 'term' && searchRef?.current) {
											searchRef.current.reset();
										}

										const hasEntityTypeDependency = Object.keys(newFilters).some((activeFilterKey) => {
											const activeFilterField = filterFields.find(
												(filterField) =>
													filterField.queryParamName === activeFilterKey || `${filterField.entityType}_question_code` === activeFilterKey,
											);
											return activeFilterField?.needsEntityType;
										});
										if (key === 'document_codes' && (!newFilters['document_codes'] || newFilters['document_codes'].length === 0)) {
											switch (value) {
												case 'PA_AVVISO':
												case 'PA_GIUSTIFICATIVO':
													delete newFilters['invoicepayment_has_document'];
													break;
												default:
													console.warn('Unknown document code value:', value);
											}
										}

										if (!hasEntityTypeDependency) {
											const { entity_type, ...filtersWithoutEntity } = newFilters;
											setQueryParams(wrapQueryParams(filtersWithoutEntity));

											// handleFilterRedirect(filtersWithoutEntity, isCompleteList);

											return filtersWithoutEntity;
										}

										setQueryParams(wrapQueryParams(newFilters));

										// handleFilterRedirect(newFilters, isCompleteList);

										return newFilters;
									});
								}}
								key={`${key}-${value}`}
								className="btn custom-outline-button"
								style={{
									textTransform: 'none',
									borderRadius: '20px',
									display: 'flex',
									alignItems: 'center',
									gap: '0.5rem',
								}}
							>
								<div>
									{filterFields.find((field) => field.queryParamName === key)?.label || getAdvancedOptionLabelByKeyAndValue(key, value)}:{' '}
									<span
										style={{
											fontWeight: '400',
										}}
									>
										{(() => {
											const optionsTypesToExclude = ['static', 'dynamic'];
											const fieldValue = filterFields
												.filter((field) => optionsTypesToExclude.includes(field.optionsType))
												.flatMap((field) => field.options)
												.find((option) => option.value.toString() === value)?.label;
											if (key === 'assigned' && (fieldValue === undefined || fieldValue === `${myUser.firstName} ${myUser.lastName}`)) {
												return 'me';
											}

											return fieldValue || getBooleanOptionLabelByKeyAndValue(key, value);
										})()}
									</span>
								</div>
								<Trash style={{ fill: '#cc334c' }} />
							</Button>
						))}
					</Fragment>
				))}

				<Button
					onClick={() => {
						setActiveFilters((prevActiveFilters) => {
							if (searchRef?.current) {
								searchRef.current.reset();
							}
							const newFilters = Object.fromEntries(Object.entries(prevActiveFilters).filter(([key]) => key === 'sampling'));
							setQueryParams(wrapQueryParams(newFilters));
							// handleFilterRedirect(newFilters, isCompleteList);
							return newFilters;
						});
					}}
					className="btn custom-ghost-error-button"
					style={{
						textTransform: 'none',
						borderRadius: '20px',
						display: 'flex',
						alignItems: 'center',
						gap: '0.5rem',
					}}
				>
					rimuovi tutti i filtri
					<Trash />
				</Button>
			</div>
		</div>
	) : null;
};

export default FiltersToDisplay;
